import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import ContactManagerPanel, { type ContactManagerItem } from "@/components/contacts/ContactManagerPanel";
import {
  createPartner,
  deleteAllPartnersByBusinessType,
  fetchPartners,
  getPartner,
  updatePartner,
  type PartnerListItem,
  type Partner,
} from "@/lib/partnersApi";
import { deleteAllClients, fetchClients, getClient, deleteClient, type ClientListItem, type Client as ApiClient } from "@/lib/clientsApi";
import ClientForm from "@/components/clients/ClientForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import { Plus, Search, UserPlus, Pencil, Trash2 } from "lucide-react";

type ClientMode = "partner" | "final";
type PartnerSegment = "agency" | "operator" | "final-client";
type PartnerContactForm = ContactManagerItem;

const Partners = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuth();
  const { toast } = useToast();

  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState<ApiClient | null>(null);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [savingPartner, setSavingPartner] = useState(false);
  const [partnerForm, setPartnerForm] = useState<{
    name: string;
    business_type: "operator" | "agency" | "";
    country: string;
    address: string;
  }>({
    name: "",
    business_type: "",
    country: "",
    address: "",
  });
  const [partnerContacts, setPartnerContacts] = useState<PartnerContactForm[]>([]);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const canAccess = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canCreate = canAccess;
  const canEditPartner = canAccess;
  const canEditClient = canAccess;
  const canDeleteClient = userRole === "ceo" || userRole === "general_manager";
  const canDeleteAll = userRole === "ceo";

  const segment: PartnerSegment =
    location.pathname === "/partners/operator"
      ? "operator"
      : location.pathname === "/partners/final-client" || location.pathname === "/partners/final-clients"
        ? "final-client"
        : "agency";
  const mode: ClientMode = segment === "final-client" ? "final" : "partner";
  const companyTypeFilter: "agency" | "operator" | null = segment === "final-client" ? null : segment;

  useEffect(() => {
    if (!canAccess) {
      navigate("/dashboard");
      return;
    }
    loadPartners();
  }, [canAccess, navigate]);

  useEffect(() => {
    if (!canAccess) return;
    loadClients();
  }, [mode, selectedPartnerId, canAccess]);

  const loadPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await fetchPartners({ page: 1, size: 2000, sortField: "name", sortOrder: "asc" });
      const items = response.items || [];
      setPartners(items);
    } catch (error: any) {
      toast({ title: "Error loading partners", description: error.message, variant: "destructive" });
    } finally {
      setLoadingPartners(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      if (mode === "partner") {
        if (!selectedPartnerId) {
          setClients([]);
          return;
        }
        const response = await fetchClients({
          page: 1,
          size: 2000,
          sortField: "first_name",
          sortOrder: "asc",
          partnerId: selectedPartnerId,
        });
        setClients(response.items || []);
        return;
      }

      const response = await fetchClients({
        page: 1,
        size: 2000,
        sortField: "first_name",
        sortOrder: "asc",
      });
      const items = response.items || [];
      setClients(items.filter((item) => item.partner_id == null));
    } catch (error: any) {
      toast({ title: "Error loading clients", description: error.message, variant: "destructive" });
    } finally {
      setLoadingClients(false);
    }
  };

  const partnersForSegment = useMemo(() => {
    if (!companyTypeFilter) return [];
    return partners.filter((partner) => partner.business_type === companyTypeFilter);
  }, [partners, companyTypeFilter]);

  const selectedPartner = useMemo(
    () => partnersForSegment.find((partner) => partner.id === selectedPartnerId) ?? null,
    [partnersForSegment, selectedPartnerId],
  );

  const filteredPartners = useMemo(() => {
    const term = partnerSearch.trim().toLowerCase();
    if (!term) return partnersForSegment;
    return partnersForSegment.filter((partner) =>
      [partner.name, partner.country, partner.address]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [partnersForSegment, partnerSearch]);

  useEffect(() => {
    if (mode !== "partner") return;
    if (partnersForSegment.length === 0) {
      if (selectedPartnerId !== null) {
        setSelectedPartnerId(null);
      }
      return;
    }
    const existsInSegment = selectedPartnerId != null && partnersForSegment.some((partner) => partner.id === selectedPartnerId);
    if (!existsInSegment) {
      setSelectedPartnerId(partnersForSegment[0].id);
    }
  }, [mode, partnersForSegment, selectedPartnerId]);

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((client) =>
      [client.first_name, client.last_name, client.company, client.email, client.mobile]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [clients, clientSearch]);

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowClientForm(true);
  };

  const handleOpenCreatePartner = () => {
    resetPartnerDialogState();
    if (companyTypeFilter) {
      setPartnerForm((prev) => ({
        ...prev,
        business_type: companyTypeFilter,
      }));
    }
    setShowPartnerForm(true);
  };

  const handleEditClient = async (clientId: number) => {
    try {
      const data = await getClient(clientId);
      setEditingClient(data);
      setShowClientForm(true);
    } catch (error: any) {
      toast({ title: "Error loading client", description: error.message, variant: "destructive" });
    }
  };

  const handleClientSuccess = async () => {
    setShowClientForm(false);
    setEditingClient(null);
    await loadClients();
  };

  const handleEditPartner = async (partnerId: number) => {
    try {
      const data = await getPartner(partnerId);
      const mappedContacts = (data.contacts || []).map((contact) => ({
        name: contact.name || "",
        position: contact.position || "",
        email: contact.email || "",
        telephone: contact.telephone || "",
        is_primary: Boolean(contact.is_primary),
        no_response: Boolean(contact.no_response),
      }));
      if (mappedContacts.length > 0 && !mappedContacts.some((contact) => contact.is_primary)) {
        mappedContacts[0] = { ...mappedContacts[0], is_primary: true };
      }
      setEditingPartner(data);
      setPartnerForm({
        name: data.name || "",
        business_type: (data.business_type as "operator" | "agency" | undefined) ?? "",
        country: data.country || "",
        address: data.address || "",
      });
      setPartnerContacts(mappedContacts);
      setShowPartnerForm(true);
    } catch (error: any) {
      toast({ title: "Error loading partner", description: error.message, variant: "destructive" });
    }
  };

  const handleSubmitPartner = async () => {
    const isEditingPartner = Boolean(editingPartner?.id);
    if (isEditingPartner && !editingPartner?.id) return;
    if (!partnerForm.name.trim()) {
      toast({ title: "Validation error", description: "Partner name is required.", variant: "destructive" });
      return;
    }
    if (!partnerForm.business_type) {
      toast({ title: "Validation error", description: "Business type is required.", variant: "destructive" });
      return;
    }

    try {
      setSavingPartner(true);
      const payload: Partial<Partner> = {
        name: partnerForm.name.trim(),
        business_type: partnerForm.business_type,
        country: partnerForm.country.trim() || null,
        address: partnerForm.address.trim() || null,
        contacts: partnerContacts.map((contact) => ({
          name: contact.name?.trim() || null,
          position: contact.position?.trim() || null,
          email: contact.email?.trim() || null,
          telephone: contact.telephone?.trim() || null,
          is_primary: Boolean(contact.is_primary),
          no_response: Boolean(contact.no_response),
        })),
      };

      const savedPartner = isEditingPartner
        ? await updatePartner(editingPartner.id, payload)
        : await createPartner(payload);

      toast({
        title: isEditingPartner ? "Partner updated" : "Partner created",
        description: isEditingPartner
          ? "Partner information saved successfully."
          : "Partner created successfully.",
      });
      setShowPartnerForm(false);
      resetPartnerDialogState();
      await loadPartners();
      if (!isEditingPartner) {
        navigate(savedPartner.business_type === "operator" ? "/partners/operator" : "/partners/agency");
        setSelectedPartnerId(savedPartner.id);
        setClientSearch("");
      }
    } catch (error: any) {
      toast({
        title: isEditingPartner ? "Error updating partner" : "Error creating partner",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSavingPartner(false);
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    if (!canDeleteClient) return;
    const confirmed = window.confirm("Delete this client? This action cannot be undone.");
    if (!confirmed) return;

    try {
      await deleteClient(clientId);
      toast({ title: "Client deleted", description: "The client was deleted successfully." });
      await loadClients();
    } catch (error: any) {
      toast({ title: "Error deleting client", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      if (mode === "final") {
        await deleteAllClients({ partnerId: "none" });
        toast({
          title: "All final clients deleted",
          description: "All final client records were removed from this segment.",
        });
        await loadClients();
      } else {
        if (!companyTypeFilter) {
          return;
        }
        await deleteAllPartnersByBusinessType(companyTypeFilter);
        toast({
          title: `All ${companyTypeLabelPlural.toLowerCase()} deleted`,
          description: `${companyTypeLabelPlural} and their clients were removed from this segment.`,
        });
        setSelectedPartnerId(null);
        await loadPartners();
      }
      setDeleteAllDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Error deleting data",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const resetPartnerDialogState = () => {
    setEditingPartner(null);
    setSavingPartner(false);
    setPartnerForm({
      name: "",
      business_type: "",
      country: "",
      address: "",
    });
    setPartnerContacts([]);
  };

  const normalizePartnerContacts = (items: PartnerContactForm[]) =>
    items.map((contact) => ({
      ...contact,
      is_primary: Boolean(contact.is_primary),
      no_response: Boolean(contact.no_response),
    }));

  const handlePartnerContactsChange = (nextContacts: PartnerContactForm[]) => {
    setPartnerContacts(normalizePartnerContacts(nextContacts));
  };

  const getClientProfileLabel = () => {
    if (mode === "final") {
      return "Final Client";
    }
    if (selectedPartner?.business_type === "operator") {
      return "Operator";
    }
    return "Agency";
  };

  const getBusinessTypeLabel = (type?: string | null) => {
    if (type === "operator") return "Operator";
    if (type === "agency") return "Agency";
    return "Not set";
  };

  const companyTypeLabelSingular = companyTypeFilter === "operator" ? "Operator" : "Agency";
  const companyTypeLabelPlural = companyTypeFilter === "operator" ? "Operators" : "Agencies";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Partners</h1>
            <p className="text-muted-foreground">
              Manage partner companies and their clients
            </p>
          </div>
        </div>

        <div className={`grid gap-4 ${mode === "partner" ? "lg:grid-cols-[360px_1fr]" : "lg:grid-cols-1"}`}>
          {mode === "partner" && (
          <Card className="h-fit">
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle>{companyTypeLabelPlural}</CardTitle>
                {canCreate && (
                  <Button type="button" size="sm" onClick={handleOpenCreatePartner}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add {companyTypeLabelSingular}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={partnerSearch}
                  onChange={(e) => setPartnerSearch(e.target.value)}
                  placeholder={`Search ${companyTypeLabelSingular.toLowerCase()}...`}
                  className="pl-10"
                />
              </div>
              <div className="max-h-[520px] overflow-auto space-y-2 pr-1">
                {loadingPartners ? (
                  <p className="text-sm text-muted-foreground">Loading {companyTypeLabelPlural.toLowerCase()}...</p>
                ) : filteredPartners.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No {companyTypeLabelPlural.toLowerCase()} found.</p>
                ) : (
                  filteredPartners.map((partner) => (
                    <div
                      key={partner.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedPartnerId(partner.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedPartnerId(partner.id);
                        }
                      }}
                      className={`w-full rounded-md border p-3 text-left transition-colors ${
                        selectedPartnerId === partner.id && mode === "partner"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/40"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium">{partner.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {[partner.country, partner.address].filter(Boolean).join(" • ") || "-"}
                          </p>
                        </div>
                        {canEditPartner && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full shrink-0"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditPartner(partner.id);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="mt-2">
                        <Badge variant="secondary">{getBusinessTypeLabel(partner.business_type)}</Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>
                  {mode === "partner"
                    ? `${companyTypeLabelSingular} Clients • ${selectedPartner?.name ?? `Select an ${companyTypeLabelSingular.toLowerCase()}`}`
                    : "Final Clients (No Partner)"}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {canCreate && (
                    <Button
                      type="button"
                      onClick={handleCreateClient}
                      disabled={mode === "partner" && !selectedPartnerId}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      {mode === "partner" ? `Add ${companyTypeLabelSingular} Client` : "Add Final Client"}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search client by name, company, email, phone..."
                  className="pl-10"
                />
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Contact</TableHead>
                      {canDeleteClient && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingClients ? (
                      <TableRow>
                        <TableCell colSpan={canDeleteClient ? 4 : 3} className="text-center text-muted-foreground">
                          Loading clients...
                        </TableCell>
                      </TableRow>
                    ) : mode === "partner" && !selectedPartnerId ? (
                      <TableRow>
                        <TableCell colSpan={canDeleteClient ? 4 : 3} className="text-center text-muted-foreground">
                          {`Select an ${companyTypeLabelSingular.toLowerCase()} to view clients.`}
                        </TableCell>
                      </TableRow>
                    ) : filteredClients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canDeleteClient ? 4 : 3} className="text-center text-muted-foreground">
                          No clients found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredClients.map((client) => (
                        <TableRow
                          key={client.id}
                          className={canEditClient ? "cursor-pointer hover:bg-muted/50" : ""}
                          onClick={() => {
                            if (canEditClient) handleEditClient(client.id);
                          }}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {client.first_name} {client.last_name}
                              </p>
                              {client.company && (
                                <p className="text-xs text-muted-foreground">{client.company}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{getClientProfileLabel()}</Badge>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{client.email || "-"}</p>
                            <p className="text-xs text-muted-foreground">{client.mobile || ""}</p>
                          </TableCell>
                          {canDeleteClient && (
                            <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteClient(client.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ClientForm
        open={showClientForm}
        onOpenChange={setShowClientForm}
        onSuccess={handleClientSuccess}
        onCreate={() => handleClientSuccess()}
        initialData={editingClient}
        defaultPartnerId={mode === "partner" ? selectedPartnerId : null}
        forceFinalClient={mode === "final"}
      />

      <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mode === "final"
                ? "Delete all final clients"
                : `Delete all ${companyTypeLabelPlural.toLowerCase()}`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mode === "final"
                ? "This will permanently delete all final clients in this segment. This action cannot be undone."
                : `This will permanently delete all ${companyTypeLabelPlural.toLowerCase()} and their clients in this segment. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllConfirm}
              disabled={deletingAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingAll ? "Deleting..." : "Delete all"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showPartnerForm}
        onOpenChange={(open) => {
          setShowPartnerForm(open);
          if (!open) {
            resetPartnerDialogState();
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartner?.id ? "Edit Partner" : "Add Partner"}</DialogTitle>
            <DialogDescription>
              {editingPartner?.id
                ? "Update partner information."
                : "Create a new partner company to associate clients with it."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="space-y-2">
                <Label>Partner Name *</Label>
                <Input
                  value={partnerForm.name}
                  onChange={(event) =>
                    setPartnerForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Partner name"
                  disabled={savingPartner}
                />
              </div>

              <div className="space-y-2">
                <Label>Business Type *</Label>
                <Select
                  value={partnerForm.business_type || undefined}
                  onValueChange={(value) =>
                    setPartnerForm((prev) => ({
                      ...prev,
                      business_type: value as "operator" | "agency",
                    }))
                  }
                >
                  <SelectTrigger disabled={savingPartner}>
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Country</Label>
                  {partnerForm.country && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setPartnerForm((prev) => ({
                          ...prev,
                          country: "",
                        }))
                      }
                      disabled={savingPartner}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <SearchableSelect
                  items={COUNTRY_OPTIONS}
                  value={partnerForm.country || undefined}
                  onValueChange={(value) =>
                    setPartnerForm((prev) => ({
                      ...prev,
                      country: String(value),
                    }))
                  }
                  placeholder="Select a country"
                  emptyText="No country found."
                  fallbackDisplayValue={partnerForm.country || undefined}
                  disabled={savingPartner}
                />
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={partnerForm.address}
                  onChange={(event) =>
                    setPartnerForm((prev) => ({
                      ...prev,
                      address: event.target.value,
                    }))
                  }
                  placeholder="Address"
                  disabled={savingPartner}
                />
              </div>
            </div>

            <ContactManagerPanel
              contacts={partnerContacts}
              disabled={savingPartner}
              onChange={handlePartnerContactsChange}
              onMissingInfo={(payload) => {
                toast({
                  title: payload.title,
                  description: payload.description,
                  variant: "destructive",
                });
              }}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPartnerForm(false)}
              disabled={savingPartner}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmitPartner}
              disabled={savingPartner || !partnerForm.name.trim() || !partnerForm.business_type}
            >
              {savingPartner ? (editingPartner?.id ? "Saving..." : "Creating...") : editingPartner?.id ? "Save" : "Create Partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Partners;
